import { useState } from 'react';
import styled from 'styled-components';
import Container from '~/grid/Container';
import Icon from '../../../atoms/Icon';
import Col from '~/grid/Col';
import BenefitsTile from './BenefitsTile';
import { Headline600Skin } from '../../../atoms/Headline/HeadlineSkins';
import Row from '~/grid/Row';
import { gridGutter } from '~/styles/grid';
import { useIntl } from 'react-intl';

const Wrapper = styled.div`
  background: ${(props) => props.theme.color.gray50};
  width: 100%;
  padding: 45px 0;

  @media (${(props) => props.theme.benefits.mobileSwitch}) {
    display: none;
  }
`;

const Headline = styled.h2`
  ${Headline600Skin};
  margin-bottom: 35px;
`;

const InitialBenefits = styled(Row)`
  justify-content: flex-start;
  margin-bottom: ${gridGutter / 2}px;
`;

const HiddenBenefits = styled(Row)`
  display: ${(props) => (props.isVisible ? 'flex' : 'none')};
  justify-content: flex-start;
`;

const Button = styled.div`
  position: relative;
  color: ${(props) => props.theme.color.primary800};
  width: 100%;
  text-align: left;
  margin-top: 35px;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: color 0.25s ease-out;

  &:hover {
    color: ${(props) => props.theme.color.cta};
  }
`;

const IconWrapper = styled.div`
  transform: ${(props) => (props.active ? 'rotate(-90deg)' : 'rotate(90deg)')};
  width: 13px;
  transform-origin: center;
  margin: -3px 10px 0 0;
`;

/**
 * @param {BenefitProps} item
 * @param {number} idx
 */
const renderTile = (item, idx) => {
  return (
    <Col
      key={`${item.title}_${idx}`}
      colAmount={{
        xl: 4,
        lg: 4,
        md: 4,
        sm: 6,
        xs: 12,
      }}
    >
      <BenefitsTile
        title={item.title}
        description={item.description}
        icon={item.icon}
      />
    </Col>
  );
};

/**
 * @param {BenefitsProps} props
 */
const DesktopBenefits = ({ items, title }) => {
  const [showAdditionalBenefits, toggleAdditionalBenefitsVisibility] =
    useState(false);
  const intl = useIntl();
  const initialBenefitsAmount = 3;
  const initialBenefitsId = 'initialBenefitsId';

  const handleClick = () => {
    toggleAdditionalBenefitsVisibility(!showAdditionalBenefits);

    if (showAdditionalBenefits) {
      setTimeout(() => {
        const elmnt = document.getElementById(initialBenefitsId);
        if (elmnt) {
          elmnt.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  };

  const additionalBenefitsText = showAdditionalBenefits
    ? intl.formatMessage({
        id: 'benefitsCTAactiveText',
      })
    : intl.formatMessage(
        { id: 'benefitsCTAinactiveText' },
        { benefitsCount: String(items.length) },
      );

  return (
    <Wrapper>
      <Container id={initialBenefitsId}>
        {title && <Headline>{title}</Headline>}
        {items && items.length > 0 && (
          <InitialBenefits>
            {items.map((initialItem, idx) => {
              if (idx <= initialBenefitsAmount - 1) {
                return renderTile(initialItem, idx);
              }
              return null;
            })}
          </InitialBenefits>
        )}
        {items && items.length > initialBenefitsAmount && (
          <HiddenBenefits isVisible={showAdditionalBenefits}>
            {items.map((initialItem, idx) => {
              if (idx > initialBenefitsAmount - 1) {
                return renderTile(initialItem, idx);
              }
              return null;
            })}
          </HiddenBenefits>
        )}
        {items && items.length > initialBenefitsAmount && (
          <Button
            role="button"
            title={additionalBenefitsText}
            active={showAdditionalBenefits}
            onClick={() => handleClick()}
          >
            <IconWrapper active={showAdditionalBenefits}>
              <Icon icon="ARROW_CTA" height={13} width={32} />
            </IconWrapper>
            <div>{additionalBenefitsText}</div>
          </Button>
        )}
      </Container>
    </Wrapper>
  );
};

export default DesktopBenefits;
